import { fetchWithSupabaseAuth } from "./supabaseClient.js";
import { onPreferencesLeave, PREFERENCES_FOR_HEADER, readPreferencesTarget } from "../utils/preferencesDelegation.js";
import { subscribeUserUiPreferences } from "./userUiPreferencesSync.js";

const ENDPOINT = "/api/me/ui-preferences/browser";
// Liste explicite : aucun jeton, brouillon métier ou état technique du navigateur n'est synchronisé.
const SETTINGS = [
  ["local", "grain.rightPanel.collapsed", "right_panel"],
  ["local", "grain.dispatch-display-menu.folded", "dispatch_menu"],
  ["local", "grain-saas:results:evening-rail-width:v1:", "evening_width", true],
  ["session", "grain-saas:results:evening-rail-visibility:v1:", "evening_visible", true],
  ["session", "grain.dispatch-product-tags:", "product_tags", true],
  ["session", "grain-saas:consolidation:view:v1:", "consolidation_view", true, "{}"],
  ["session", "grain:geotab-session", "geotab_session", false, "{}"],
  ["session", "grain-saas:data-manager:table-sort:v1", "table_sort", false, "{}"],
  ["session", "grain-saas:data-manager:table-scroll:v1", "table_scroll", false, "{}"],
  ["session", "grain-saas:data-manager:frozen-panes:v2", "frozen_panes", false, "{}"],
  ["local", "grain-saas:fleet-floating-route-map:v1", "floating_map"],
  ["local", "grain.reports.truckSheet.onCallDispatcher", "reports_dispatcher"],
  ["session", "grain:dispatch-results-context", "dispatch_context"],
  ["local", "journal-local-engine-texts", "codex_engine_texts"],
  ["local", "journal-local-engine-image", "codex_engine_image"],
];
// Admin qui règle pour un autre : la journée ouverte et le défilement restent dans son onglet, le reste va à l'utilisateur.
const TAB_ONLY_FIELDS = {
  dispatch_context: () => true,
  table_scroll: () => true,
  consolidation_view: (field) => /^(from|to|select|selectView|selectDay|scroll:.*)$/.test(field),
  geotab_session: (field) => field === "date" || field === "period",
};

function parseObject(raw) {
  try {
    const value = JSON.parse(raw ?? "{}");
    return value && typeof value === "object" && !Array.isArray(value) ? value : {};
  } catch { return {}; }
}
let active = null;
const storageListeners = new Map();

function notifyStorage(previous, next) {
  const changed = [...new Set([...Object.keys(previous), ...Object.keys(next)])].filter((key) => previous[key] !== next[key]);
  if (changed.length) queueMicrotask(() => changed.forEach((key) => storageListeners.get(key)?.forEach((listener) => listener())));
}

export function subscribeUserUiPreferenceStorage(kind, key, listener) {
  const name = settingFor(kind, key)?.[2];
  if (!name) return () => {};
  if (!storageListeners.has(name)) storageListeners.set(name, new Set());
  storageListeners.get(name).add(listener);
  return () => storageListeners.get(name)?.delete(listener);
}

function nativeStorage(kind, targetWindow = typeof window === "undefined" ? undefined : window) {
  try { return targetWindow?.[`${kind}Storage`] ?? null; } catch { return null; }
}

function settingFor(kind, key) {
  return SETTINGS.find(([storageKind, storageKey, , prefix]) => storageKind === kind
    && (prefix ? String(key).startsWith(storageKey) : key === storageKey));
}

async function request(binding, method = "GET", values = {}, claim = false, keepalive = false) {
  const response = await binding.fetchImpl(`${ENDPOINT}${claim ? "/claim-missing" : ""}`, {
    method,
    headers: { "Content-Type": "application/json", [PREFERENCES_FOR_HEADER]: binding.userId },
    ...(method !== "GET" ? { body: JSON.stringify({ values }), keepalive } : {}),
    signal: keepalive ? undefined : binding.signal,
  });
  if (!response.ok) throw new Error("Impossible de synchroniser les réglages d’interface du compte sélectionné.");
  const result = await response.json();
  if (!result?.values || typeof result.values !== "object" || Array.isArray(result.values)) {
    throw new Error("Les réglages d’interface reçus sont invalides.");
  }
  return result.values;
}

function flush(binding, keepalive = false) {
  clearTimeout(binding.timer);
  binding.timer = null;
  if (binding.saving) return binding.saving.then(() => flush(binding, keepalive));
  if (!Object.keys(binding.pending).length) return Promise.resolve();
  const patch = binding.pending;
  binding.pending = {};
  binding.saving = request(binding, "PATCH", patch, false, keepalive)
    .then((values) => {
      binding.confirmed = values;
      binding.values = { ...values, ...binding.pending };
    })
    .catch((error) => {
      const previous = binding.values;
      binding.values = { ...binding.confirmed, ...binding.pending };
      notifyStorage(previous, binding.values);
      binding.onError?.(error);
      throw error;
    })
    .finally(() => { binding.saving = null; });
  return binding.saving.then(() => Object.keys(binding.pending).length ? flush(binding, keepalive) : undefined);
}

export async function initializeUserUiPreferenceStorage({
  userId, delegated = false, onError, signal, fetchImpl = fetchWithSupabaseAuth, targetWindow,
}) {
  const binding = { userId, delegated, onError, signal, fetchImpl, values: {}, local: {}, confirmed: {}, pending: {}, saving: null, timer: null };
  let values = await request(binding);
  if (!delegated) {
    const missing = {};
    for (const [kind, key, name, prefix] of SETTINGS) {
      if (Object.hasOwn(values, name)) continue;
      const legacyKey = prefix ? key + (name === "evening_width" || name === "evening_visible" ? encodeURIComponent(userId) : userId) : key;
      try {
        const legacy = nativeStorage(kind, targetWindow)?.getItem(legacyKey);
        if (legacy != null) missing[name] = legacy;
      } catch { /* Les réglages serveur restent disponibles si le stockage local est bloqué. */ }
    }
    if (Object.keys(missing).length) values = await request(binding, "POST", missing, true);
  }
  if (signal?.aborted) return () => {};
  binding.values = binding.confirmed = values;
  active = binding;
  const unregister = onPreferencesLeave(() => flush(binding));
  const unregisterSync = subscribeUserUiPreferences("browser", (incoming) => {
    if (binding.saving || Object.keys(binding.pending).length) return false;
    const previous = binding.values;
    binding.values = binding.confirmed = { ...incoming };
    notifyStorage(previous, binding.values);
    return true;
  });
  const pagehide = () => { void flush(binding, true).catch(() => {}); };
  targetWindow ||= typeof window === "undefined" ? undefined : window;
  targetWindow?.addEventListener?.("pagehide", pagehide);
  return () => {
    unregister();
    unregisterSync();
    targetWindow?.removeEventListener?.("pagehide", pagehide);
    clearTimeout(binding.timer);
    if (active === binding) active = null;
    void flush(binding, true).catch(() => {});
  };
}

// Adaptateur limité aux réglages listés ci-dessus. Le stockage natif de l'Admin n'est jamais réécrit.
export function userUiPreferenceStorage(kind) {
  const storage = nativeStorage(kind);
  if (!active) {
    if (!readPreferencesTarget()) return storage;
    // Les loaders du routeur peuvent lire le stockage avant le montage des providers.
    // Aucun repli sur les valeurs de l'Admin pendant le chargement du compte choisi.
    return {
      getItem: (key) => settingFor(kind, key)?.[4] ?? null,
      setItem() {}, removeItem() {}, key: () => null, length: 0,
    };
  }
  const binding = active;
  const read = (name) => (Object.keys(binding.local[name] ?? {}).length
    ? JSON.stringify({ ...parseObject(binding.values[name]), ...binding.local[name] })
    : binding.values[name]);
  function set(key, value) {
    const setting = settingFor(kind, key);
    if (!setting) return value == null ? storage?.removeItem(key) : storage?.setItem(key, value);
    const name = setting[2];
    let next = value == null ? null : String(value);
    const before = read(name);
    if ((before ?? null) === next) return;
    const tabOnly = binding.delegated && TAB_ONLY_FIELDS[name];
    if (tabOnly) {
      // Les champs de journée et de défilement restent ici ; l'utilisateur garde les siens.
      const incoming = parseObject(next), stored = parseObject(binding.values[name]);
      const pick = (kept) => Object.fromEntries(Object.entries(incoming).filter(([field]) => tabOnly(field) === kept));
      binding.local = { ...binding.local, [name]: pick(true) };
      const shared = { ...stored, ...pick(false) };
      next = binding.values[name] == null && !Object.keys(shared).length ? null : JSON.stringify(shared);
    }
    if ((binding.values[name] ?? null) !== next) {
      binding.values = { ...binding.values, [name]: next };
      binding.pending[name] = next;
      clearTimeout(binding.timer);
      binding.timer = setTimeout(() => { void flush(binding).catch(() => {}); }, 400);
    }
    notifyStorage({ [name]: before }, { [name]: read(name) });
  }
  const keys = () => SETTINGS.filter(([storageKind]) => storageKind === kind)
    .map(([, key, , prefix]) => prefix ? key + binding.userId : key);
  return {
    getItem(key) {
      const setting = settingFor(kind, key);
      if (!setting) {
        // L'ancien gel des colonnes ne doit pas importer les colonnes de l'Admin.
        if (key === "grain-saas:data-manager:frozen-panes:v1") return null;
        return storage?.getItem(key) ?? null;
      }
      return read(setting[2]) ?? setting[4] ?? null;
    },
    setItem: set,
    removeItem: (key) => set(key, null),
    key: (index) => keys()[index] ?? null,
    get length() { return keys().length; },
  };
}
